'use client';

import React, { startTransition, useCallback, useDeferredValue, useState } from 'react';
import {
  DictsGateState,
  OnChangeIsFreeType,
  OnChangePriceType,
  OnUpdateCategoriesType,
  OnUpdateParamType,
  PriceValue,
  ShownParams,
} from '../SearchFiltersList/types';
import { ParamItem } from '@/utils/SEOPathModule/convertPathToParams';
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useMediaQuerySafe } from '@/hooks/useMediaQuerySafe';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { MobileFiltersList } from '../SearchFiltersList/MobileFiltersList';
import { Button } from '@/components/ui/button';

type Props = {
  shownParameters: ShownParams;
  params: ParamItem[];
  price: PriceValue;
  categories: string[];
  onChangePrice: OnChangePriceType;
  isFree: boolean;
  onChangeIsFree: OnChangeIsFreeType;
  onUpdateParam: OnUpdateParamType;
  onUpdateCategory: OnUpdateCategoriesType;
  onClear?: () => void;
  onSubmit: () => void;
  onChangeSort: (newSort: string) => void;
  sort: string;
  dictsGateState?: DictsGateState;
  showFree?: boolean;
};

export const MobileSearchForm: React.FC<Props> = ({
  categories,
  onUpdateCategory,
  shownParameters,
  params,
  onUpdateParam,
  price,
  onChangePrice,
  isFree,
  onChangeIsFree,
  onSubmit,
  onChangeSort,
  onClear,
  sort,
  dictsGateState = 'idle',
  showFree = false,
}) => {
  const t = useTranslations('filters');
  const [isOpen, setIsOpen] = useState(false);
  const isContentReady = useDeferredValue(isOpen) || !isOpen;
  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);
  const onOpenChange = useCallback((open: boolean) => {
    if (open) {
      startTransition(() => {
        setIsOpen(true);
      });
    } else {
      setIsOpen(false);
    }
  }, []);
  const isTablet = useMediaQuerySafe('(min-width: 768px)');

  const trigger = (
    <Button
      variant='ghost'
      size='icon'
      className='md:h-[52px] md:w-[52px] h-[36px] w-[36px] bg-surface-light hover:bg-surface-light active:scale-100'
    >
      <Image
        width={32}
        height={32}
        src='/assets/icons/sliders.svg'
        alt='sle'
        className='md:w-[32px] md:h-[32px] w-[24px] h-[24px]'
      />
    </Button>
  );

  const filters = isContentReady && (
    <MobileFiltersList
      showFree={showFree}
      params={params}
      shownParameters={shownParameters}
      price={price}
      isFree={isFree}
      categories={categories}
      onChangePrice={onChangePrice}
      onChangeIsFree={onChangeIsFree}
      onUpdateCategory={onUpdateCategory}
      onUpdateParam={onUpdateParam}
      onClear={onClear}
      sort={sort}
      onChangeSort={onChangeSort}
      onSubmit={onSubmit}
      onClose={onClose}
      dictsGateState={dictsGateState}
    />
  );

  if (isTablet) {
    return (
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent
          side='right'
          showCloseButton={false}
          aria-describedby={undefined}
          className='w-[420px] gap-0 overflow-hidden border-primary-stroke-day p-0 sm:max-w-[420px]'
        >
          <SheetTitle className='sr-only'>{t('filter')}</SheetTitle>
          {filters}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent
        aria-describedby={undefined}
        className='h-[94dvh] overflow-hidden rounded-t-[20px] border-0 data-[vaul-drawer-direction=bottom]:max-h-[94dvh] data-[vaul-drawer-direction=bottom]:rounded-t-[20px]'
      >
        <DrawerTitle className='sr-only'>{t('filter')}</DrawerTitle>
        <div className='min-h-0 flex-1'>{filters}</div>
      </DrawerContent>
    </Drawer>
  );
};
